import { Component, h, Listen, State } from '@stencil/core';
import DOMPurify from 'dompurify';
import dialogPolyfill from 'dialog-polyfill';
import { CONFIG_URL } from '../../constant';
import { Config, ConfigDate, idpLoginUserInfo, User } from '../../beans';
import { userIsSchoolTeacher, parseJwt, isValidDate } from '../../utils';
import { sendClosedXapi, sendFocusedXapi } from '../../xapi';
import { AdvStorage } from '../../storage';

@Component({
  tag: 'zanichelli-adv',
  styleUrl: 'zanichelli-adv.css',
  shadow: true,
})
export class ZanichelliAdv {
  @State() userData: User;
  @State() isMobile: boolean = false;
  @State() userLoaded: boolean = false;
  @State() openDialog: boolean = false;
  @State() location: string = window.location.pathname;

  private advConfig: Config[];
  private currentConfig: null | Config;
  private doNotShowAgain = false;
  private dialogRef?: HTMLDialogElement;
  private shouldRegisterDialogPolyfill = typeof HTMLDialogElement === 'undefined';
  private interval: NodeJS.Timer;
  private navigationListener: boolean = false;

  constructor() {
    this.toggleShowMeAgain = this.toggleShowMeAgain.bind(this);
    this.toggleModalClose = this.toggleModalClose.bind(this);
    this.handleOpenLink = this.handleOpenLink.bind(this);
  }

  @Listen('load', { target: 'window' })
  @Listen('resize', { target: 'window' })
  handleWindowResize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  /** user log-in & loaded event */
  @Listen('loginSuccess', { target: 'window' })
  @Listen('userLoaded', { target: 'window' })
  userLoadedHandler(event: CustomEvent): void {
    this.userData = event.detail.token ? parseJwt(event.detail.token) : event.detail.e;
    if (event.detail.teachingGroups) this.userData.teachingGroupsCodes = event.detail.teachingGroups.map(teachingGroup => teachingGroup.code);
    this.userLoaded = true;
  }

  @Listen('userLoadedFail', { target: 'window' })
  userLoadedFailHandler(): void {
    this.userLoaded = true;
  }

  private async toggleModalClose() {
    this.openDialog = false;
    AdvStorage.addToCookie(this.currentConfig.id);
    await sendClosedXapi(this.currentConfig.id, this.doNotShowAgain, this.currentConfig.adv_body.title, this.userData ? String(this.userData.myz.id) : null);
  }

  private toggleShowMeAgain(event: CustomEvent) {
    this.doNotShowAgain = !this.doNotShowAgain;

    if (event.detail.checked) {
      AdvStorage.addToLocalStorage(this.currentConfig.id);
    } else {
      AdvStorage.removeFromLocalStorage(this.currentConfig.id);
    }
  }

  private async getUserDataFromMethod() {
    const idpLogin = document.querySelector('idp-login') as idpLoginUserInfo;
    if (!idpLogin) return null;
    const data = await idpLogin.getUserInfo();
    if (data !== undefined) this.userLoaded = true;
    if (!data) return null;
    const payload = { ...data.e };
    if (data.teachingGroups) payload.teachingGroupsCodes = data.teachingGroups.map(teachingGroup => teachingGroup.code);
    return payload;
  }

  private supportsNavigateApi() {
    return 'navigation' in window;
  }

  private pathMatches(): boolean {
    const match = this.advConfig.filter((config: Config) => {
      const location = config.locations.find(item => item.host == window.location.host);

      return location.paths.find(path => {
        if (path.endsWith('/*')) {
          if (path == '/*') return true;

          let validPath = path.substring(0, path.length - 2);
          return window.location.pathname.startsWith(validPath);
        }
        return window.location.pathname == path;
      });
    });
    return !!match.length;
  }

  private async fetchConfig() {
    return fetch(`${CONFIG_URL}`)
      .then(response => response.json())
      .then(res => {
        this.advConfig = res.filter((adv: Config) => {
          const location = adv.locations.find(item => item.host == window.location.host);
          return !!(this.checkDate(adv.dates) && location);
        });
      });
  }

  private navigateHandler = (e: NavigateEvent) => {
    this.setLocation(e.destination.url);
  };

  private setLocation = (location: string) => {
    this.location = location;
  };

  private setConfigByRoles = async () => {
    if (!(await this.isUserLoadedOrAnonymous())) return;
    const localStorageAdvIds: number[] = AdvStorage.getLocalStorage();
    const cookieAdvIds: number[] = AdvStorage.getCookie();

    this.advConfig.forEach((config: Config) => {
      if (this.currentConfig) return;
      if (localStorageAdvIds.find(id => config.id === id) || cookieAdvIds.find(id => config.id === id)) {
        return;
      }

      if (!config.roles) {
        this.currentConfig = config;
        this.openDialog = true;
        return;
      }

      if (this.userData) {
        this.userData.roles.find(userRole => {
          config.roles?.find(role => {
            if (role.role == userRole.roleName) {
              if (userIsSchoolTeacher(this.userData.roles) && role.teaching_groups && !role.teaching_groups.find(code => this.userData.teachingGroupsCodes.includes(code))) {
                this.openDialog = false;
                return;
              } else {
                this.currentConfig = config;
                this.openDialog = true;
                return;
              }
            }
          });
        });
      }
    });
  };

  private shouldAttachEventListener = () => {
    return this.openDialog;
  };

  async componentWillLoad() {
    await this.fetchConfig();
  }

  private checkDate(dates: ConfigDate) {
    if (dates) {
      return isValidDate(dates.start_date, dates.end_date);
    }

    return true;
  }

  private async isUserLoadedOrAnonymous(): Promise<boolean> {
    if (!this.userLoaded) {
      this.userData = await this.getUserDataFromMethod();
    }
    return this.userLoaded;
  }

  async componentWillRender() {
    if (this.advConfig.length) {
      if (!(await this.isUserLoadedOrAnonymous())) return;

      await this.setConfigByRoles();

      if (this.shouldAttachEventListener()) {
        if (this.supportsNavigateApi() && !this.navigationListener) {
          (window as any).navigation.addEventListener('navigate', this.navigateHandler);
          this.navigationListener = true;
        }
        if (!this.supportsNavigateApi() && !this.interval) {
          this.interval = setInterval(() => {
            this.setLocation(window.location.pathname);
          }, 1000);
        }
      }

      if (!this.pathMatches()) this.currentConfig = null;
    }
  }

  componentDidRender() {
    if (this.dialogRef) {
      if (this.shouldRegisterDialogPolyfill) {
        dialogPolyfill.registerDialog(this.dialogRef);
        this.shouldRegisterDialogPolyfill = false;
      }

      if (this.openDialog && !this.dialogRef.open) {
        this.dialogRef.showModal();
      } else if (!this.openDialog && this.dialogRef.open) {
        this.dialogRef.close();
      }
    }
  }

  private async handleOpenLink(url: string) {
    const cleanUrl = DOMPurify.sanitize(url);
    window.open(cleanUrl, '_blank');
    await sendFocusedXapi(this.currentConfig.id, cleanUrl, this.currentConfig.adv_body.title, this.userData ? String(this.userData.myz.id) : null);
  }

  disconnectedCallback() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    if (this.navigationListener) {
      window.navigation.removeEventListener('navigate', this.navigateHandler);
      this.navigationListener = false;
    }
  }

  render() {
    if (this.currentConfig) {
      const { image_url, image_url_mobile, title, main_text, ctas } = this.currentConfig.adv_body;
      return (
        <dialog
          // @ts-ignore
          onCancel={this.toggleModalClose}
          ref={el => (this.dialogRef = el as HTMLDialogElement)}
        >
          <div class="adv-background-overlay">
            <div class="adv-modal-container">
              <div class="adv-content">
                <div class="adv-image-container">
                  <img alt="" src={DOMPurify.sanitize(this.isMobile ? image_url_mobile : image_url)}></img>
                  <div class="adv-icon-mobile">
                    <z-button aria-label="Chiudi" onClick={this.toggleModalClose} variant="tertiary" size="x-small" disabled="false">
                      <z-icon alt="" width={24} height={24} name="multiply-circled-filled"></z-icon>
                    </z-button>
                  </div>
                </div>

                <div class="adv-action-container">
                  <div class="adv-text-content">
                    <div class="adv-text">
                      <h3 innerHTML={DOMPurify.sanitize(title)}></h3>
                      <p innerHTML={DOMPurify.sanitize(main_text)}></p>
                      <z-input class="adv-checkbox-label" type="checkbox" label="Non mostrare più" label-position="right" onInputCheck={this.toggleShowMeAgain}></z-input>
                    </div>
                    <div class="adv-ctas">
                      {ctas.map(cta => (
                        <button class="z-link z-link-blue z-link-sb body-3" role="link" onClick={() => this.handleOpenLink(cta.url)}>
                          {DOMPurify.sanitize(cta.text)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div class="adv-icon">
                    <z-button aria-label="Chiudi" onClick={this.toggleModalClose} variant="tertiary" size="x-small" disabled="false">
                      <z-icon alt="" width={24} height={24} name="multiply-circled-filled"></z-icon>
                    </z-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </dialog>
      );
    }
  }
}
